/* Short message primitive: empty states, hints, cautions, errors.

   Exists because these were styled ad hoc in eight places across two workspaces
   with six different treatments — 0.75/0.8/0.82/0.85rem plus one that silently
   inherited the 15px body size — and no consistent colour or a11y handling.

   `tone` drives typography, colour, icon AND the ARIA role together, so a caller
   cannot ship a red error that a screen reader never announces, or a bare string
   that looks like an error but isn't. Three tones, each earned by a real case:

     dim     empty states, hints, "not signed in", loading
     warn    a caution the user should notice but that isn't a failure
     danger  an actual error

   Layout stays with the caller. A primitive has no business deciding how much
   room a page gives its empty state, so there is no padding here — callers style
   the `g-notice` element from outside when they need it inset. */

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { shadowReset } from '../../design/base.js';
import '../g-icon/g-icon.js';

export type NoticeTone = 'dim' | 'warn' | 'danger';

@customElement('g-notice')
export class GNotice extends LitElement {
  static override styles = [
    shadowReset,
    css`
      :host {
        display: flex;
        /* flex-start, not center: a wrapped multi-line message should keep its
           icon beside the FIRST line, not floating at the vertical middle. */
        align-items: flex-start;
        gap: var(--space-2);
        color: var(--text-dim);
        font-family: var(--font-ui);
        font-size: 0.82rem;
        line-height: 1.4;
      }
      :host([tone='danger']) {
        color: var(--danger);
      }
      /* The icon carries the emphasis while the text stays quiet (§31) — a whole
         paragraph in accent would read as an error. */
      :host([tone='warn']) g-icon {
        color: var(--accent);
      }
      g-icon {
        /* nudge onto the text's optical centre rather than its box top */
        margin-top: 1px;
        --icon-size: 14px;
      }
    `,
  ];

  @property({ reflect: true }) tone: NoticeTone = 'dim';

  override render() {
    // Only a real error interrupts; announcing every empty state would be noise.
    const role = this.tone === 'danger' ? 'alert' : undefined;
    return html`
      ${this.tone === 'dim' ? nothing : html`<g-icon name="warn"></g-icon>`}
      <span role=${role ?? nothing}><slot></slot></span>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-notice': GNotice;
  }
}
